import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchBadgeCounts, totalCount, getCachedCounts, onBadgeCounts } from "./notifications";
import { fetchMe, getCachedMe } from "./me";
import "./mypage.css";
import { ToastHost } from "./Toast";

// 勤怠マイページ共通レイアウト（Porcelain シェル）。
// 左サイドバー＋コンテンツ領域。active で現在地をハイライト。
// スマホ(≤860px)ではサイドバーをドロワー化し、上部のハンバーガーで開閉する
// （SLASH ADMIN 本体と同じ方式）。最下部アカウントは /api/employee/me/ から本人情報を表示。
// 各ページは <MypageLayout active="leave" title="休み申請">…</MypageLayout> の形で使う。

const API_URL = process.env.REACT_APP_API_URL || "";

const Icon = {
  home: () => (<svg viewBox="0 0 24 24"><path d="M3 10.5 12 4l9 6.5" /><path d="M5 9.5V20h14V9.5" /><path d="M10 20v-5h4v5" strokeLinejoin="round" /></svg>),
  clock: () => (<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" /><path d="M12 8v4.5l3 2" strokeLinecap="round" /></svg>),
  calendar: () => (<svg viewBox="0 0 24 24"><rect x="3.5" y="5" width="17" height="15" rx="2.5" /><path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" strokeLinecap="round" /></svg>),
  plane: () => (<svg viewBox="0 0 24 24"><path d="M21 4 3 11l6.5 2.2L12 20l3-7.5z" strokeLinejoin="round" /></svg>),
  edit: () => (<svg viewBox="0 0 24 24"><path d="M5 19h14M6 15l9-9 3 3-9 9-3.6.6z" strokeLinejoin="round" /></svg>),
  timer: () => (<svg viewBox="0 0 24 24"><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2.5 1.5M9 3h6" strokeLinecap="round" /></svg>),
  check: () => (<svg viewBox="0 0 24 24"><path d="M4 5.5h11M4 12h11M4 18.5h6" strokeLinecap="round" /><path d="M16.5 17.5l2 2 3.5-4" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  calcheck: () => (<svg viewBox="0 0 24 24"><rect x="3.5" y="5" width="17" height="15" rx="2.5" /><path d="M3.5 9.5h17M8 3.5v3M16 3.5v3M9 14.5l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  yen: () => (<svg viewBox="0 0 24 24"><path d="M8 6h8M8 6l4 6 4-6M12 12v6M9.5 15h5" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  chevron: () => (<svg viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  back: () => (<svg viewBox="0 0 24 24"><path d="M10 18l-6-6 6-6M4 12h15" strokeLinecap="round" strokeLinejoin="round" /></svg>),
};

// adminOnly: 経営層(is_executive=True)専用。シフト設定は管理者がメンバーのシフトを
// 編集する管理画面のため、社員アカウントではメニューから隠す（直接URLでもAPIが403を返す）。
// employeeOnly: 社員本人の申請ページ。経営者は申請リスト（勤怠一覧の承認）で一括管理するため
// 経営者のメニューからは隠す（休み申請・時間訂正・残業）。
const NAV = [
  { key: "home", to: "/employee", label: "ホーム", icon: Icon.home },
  { key: "attendance", to: "/employee/attendance", label: "勤怠履歴", icon: Icon.clock },
  { key: "shift", to: "/employee/shift", label: "シフト・有休", icon: Icon.calendar, adminOnly: true },
  { key: "todo", to: "/employee/todo", label: "TODO", icon: Icon.check },
  { key: "apply", to: "/employee/apply", label: "申請", icon: Icon.plane, employeeOnly: true },
  { key: "payslip", to: "/employee/payslip", label: "給与明細", icon: Icon.yen },
];

// 既読型のバッジ（開いた＝見た、で消える）。状態由来は現在地でも消さないのでここに入れない。
const SEEN_KEYS = ["apply", "payslip"];

// count>0 のとき、項目の右端に未読件数バッジ（アプリ内バッジ・仕様2026-07 項目7）。
function NavItem({ item, active, count, onNavigate }) {
  const I = item.icon;
  return (
    <Link className={`mp-ni${active === item.key ? " mp-on" : ""}`} to={item.to} onClick={onNavigate}>
      <I /> {item.label}
      {count > 0 && <span className="mp-badge" aria-label={`未読${count}件`}>{count}</span>}
    </Link>
  );
}

// title/subtitle/actions を渡すと共通ヘッダを描画。指定しなければ children だけ。
function MypageLayout({ active, title, subtitle, actions, children }) {
  const [navOpen, setNavOpen] = useState(false);
  // 初回はキャッシュ（前回取得したロール）から復元＝遷移時に me=null に戻らずちらつかない。
  const [me, setMe] = useState(getCachedMe);
  const [team, setTeam] = useState([]);
  // 通知バッジの件数。初回はキャッシュから復元＝遷移時にバッジが一瞬消えない。
  const [counts, setCounts] = useState(getCachedCounts);
  const close = () => setNavOpen(false);

  // 各ページが refreshBadgeCounts() を呼んだら即座に反映する
  // （承認/振り分け/TODO状態変更の直後にサイドバーが古い件数のままにならない）。
  useEffect(() => onBadgeCounts(setCounts), []);

  useEffect(() => {
    let alive = true;
    fetchMe().then((d) => { if (alive && d) setMe(d); });
    // サイドバー下部に出すスタッフの勤務状況（本日）。
    fetch(`${API_URL}/api/employee/team-status/`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d) setTeam(d.results || []); })
      .catch(() => {});
    // 通知バッジ件数。画面遷移のたびに再マウントされるので都度取り直す。
    fetchBadgeCounts().then((c) => { if (alive) setCounts(c); });
    return () => { alive = false; };
  }, []);

  const name = me?.name || "";
  const initial = name ? name.slice(0, 1) : "·";
  const sub = me ? `${me.is_executive ? "経営層" : "社員"}${me.office ? `・${me.office}` : ""}` : "";

  // アプリ内通知バッジ（仕様 通知バッジ_2026-07）。件数の判断はすべてサーバ側。
  // 例外は現在地が既読型(apply/payslip)のときだけ: サーバの既読更新はそのページ自身の
  // データ取得（申請/明細のGET）の副作用で走るため、バッジ取得と順序が前後しうる。
  // 「開いている＝見ている」ので先に消しておく（次の遷移ではサーバ側も0になっている）。
  // 状態由来(home/attendance/todo)は現在地でも消さない＝残っている仕事の量は画面で変わらない。
  const shown = { ...counts };
  if (SEEN_KEYS.includes(active)) delete shown[active];
  const total = totalCount(shown);

  return (
    <div className="mp">
      {/* スマホ用トップバー（≤860px のみ表示）。ハンバーガーでドロワーを開く。
          並びは SLASH ADMIN 本体のヘッダーに合わせる（ブランド左・ハンバーガー右）。
          行き来しても親指の位置が変わらないよう、左右を入れ替えないこと。 */}
      <div className="mp-topbar">
        <div className="mp-tb-brand"><span className="mp-tb-mark">G</span>Guluu 勤怠</div>
        <button className="mp-burger" aria-label={total > 0 ? `メニューを開く（未読${total}件）` : "メニューを開く"} onClick={() => setNavOpen(true)}>
          <span></span><span></span><span></span>
          {total > 0 && <i className="mp-burger-dot" aria-hidden="true"></i>}
        </button>
      </div>

      <div className="mp-shell">
        <div className={`mp-aside${navOpen ? " mp-open" : ""}`}>
          <div className="mp-brand">
            <div className="mp-mark">G</div>
            <div className="mp-nm">Guluu<small>勤怠マイページ</small></div>
          </div>
          {/* ロール専用項目は me 確定後のみ表示（未取得中は出さない＝遷移時のちらつき防止）。
              adminOnly=経営者だけ / employeeOnly=社員だけ / それ以外=常時。 */}
          {NAV.filter((it) => {
            if (it.adminOnly) return me?.is_executive === true;
            if (it.employeeOnly) return me ? me.is_executive === false : false;
            return true;
          }).map((it) => (
            <NavItem key={it.key} item={it} active={active} count={shown[it.key] || 0} onNavigate={close} />
          ))}
          <Link className="mp-ni mp-ni-admin" to="/" onClick={close}><Icon.back /> SLASH ADMIN</Link>
          {/* サイドバー下部：スタッフの勤務状況＋本人アカウント。下から並べる。 */}
          <div className="mp-bottom">
            {team.length > 0 && (
              <div className="mp-team">
                {team.map((t) => (
                  <div className={`mp-team-row${t.status === "open" ? " mp-team-on" : ""}`} key={t.name}>
                    <span className={`mp-team-dot${t.status === "open" ? " mp-on" : ""}`}></span>
                    <span className="mp-team-name">{t.name}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="mp-acct">
              <div className="mp-avi">{initial}</div>
              <div className="mp-acct-id"><div className="mp-nm">{name || "-"}</div><small>{sub}</small></div>
              {/* 社員名の横の総数バッジ（未読の通知合計・現在地分は除く） */}
              {total > 0 && <span className="mp-badge mp-badge-total" aria-label={`未読の通知 合計${total}件`}>{total}</span>}
            </div>
          </div>
        </div>

        {/* ドロワーを開いている間のオーバーレイ（タップで閉じる）。 */}
        <div className={`mp-overlay${navOpen ? " mp-open" : ""}`} onClick={close}></div>

        <div className="mp-main">
          {(title || actions) && (
            <div className={`mp-pagehead${actions ? " mp-has-actions" : ""}`}>
              <div>
                {subtitle && <div className="mp-eyebrow">{subtitle}</div>}
                {title && <h1>{title}</h1>}
              </div>
              {actions && <div className="mp-pagehead-actions">{actions}</div>}
            </div>
          )}
          {children}
        </div>
      </div>
      <ToastHost />
    </div>
  );
}

export default MypageLayout;
